// «Мир» — landing screen of the rebuilt campaign UI: current situation, world
// state (resources + risks) and the action available in the current phase.
// Reads setup.commandHub (purpose-built summary). Isolated .gn-* markup.

import { useState } from "react";
import { type CampaignSetup } from "../api";
import { phaseInfo } from "../cycle/shared";
import { type Workspace } from "./ActionsScreen";
import { SituationModal } from "./SituationModal";

export type GnTab = "world" | "actions" | "chronicle" | "hq";
const GEN = "/assets/game-ui/gen";

function resourceIcon(code: string, label: string): string {
  const s = `${code} ${label}`.toLowerCase();
  if (/убеж|housing|shelter|жиль|дом|бункер/.test(s)) return "res_housing";
  if (/провиз|food|еда|прод|паёк|припас/.test(s)) return "res_food";
  if (/энерг|energy|power|топлив|электр|генерат/.test(s)) return "res_energy";
  if (/связ|signal|communi|информ|коммуник|радио/.test(s)) return "res_signal";
  if (/медик|medic|health|лекар|санитар|аптеч/.test(s)) return "res_medicine";
  return "res_food";
}
function riskIcon(code: string, label: string): string {
  const s = `${code} ${label}`.toLowerCase();
  if (/голод|hunger|провиз|еда|припас/.test(s)) return "threat_hunger";
  if (/восстан|темп|bottleneck|срыв|задерж|срок|снабж/.test(s)) return "threat_bottleneck";
  return "threat_unrest";
}
// Куда ведёт фаза-CTA «Мира»: верхняя вкладка + конкретная под-вкладка «Действий».
// Значения targetSection из бэкенда (CommandHubComposer): actions / discussion /
// projects / results / situation.
function ctaTarget(target: string): { tab: GnTab; ws?: Workspace } {
  const s = target.toLowerCase();
  if (s === "results" || s.includes("chron")) return { tab: "chronicle" };
  if (s === "situation" || s === "world" || s === "мир") return { tab: "world" };
  if (s === "discussion" || s.includes("dialog") || s.includes("meeting") || s.includes("встреч"))
    return { tab: "actions", ws: "dialogues" };
  if (s === "projects" || s.includes("initiativ") || s.includes("инициатив"))
    return { tab: "actions", ws: "initiatives" };
  if (s.includes("chat") || s.includes("negotiat") || s.includes("чат")) return { tab: "actions", ws: "chats" };
  if (s.includes("hq") || s.includes("council") || s.includes("штаб")) return { tab: "hq" };
  return { tab: "actions", ws: "actions" };
}
function movesPlural(n: number): string {
  const d = n % 10, dd = n % 100;
  if (d === 1 && dd !== 11) return "ход";
  if (d >= 2 && d <= 4 && (dd < 12 || dd > 14)) return "хода";
  return "ходов";
}

export function WorldScreen({
  setup,
  onNavigate,
}: {
  setup: CampaignSetup;
  onNavigate: (tab: GnTab, ws?: Workspace) => void;
}) {
  const [situationOpen, setSituationOpen] = useState(false);
  const hub = setup.commandHub;
  const cycle = setup.cycle.current;
  if (!hub || !cycle) {
    return (
      <div className="gn-stub">
        <img src={`${GEN}/nav_world.png`} alt="" />
        <h3>Мир формируется</h3>
        <p>Как только мир и роли будут готовы, здесь появится сводка.</p>
      </div>
    );
  }
  const remaining = Math.max(0, cycle.actionLimit - cycle.actionsUsed);
  const canAct = cycle.phase === "DAY" && cycle.canAct;

  return (
    <div className="gn-body">
      <button type="button" className="gn-panel gn-hero" onClick={() => setSituationOpen(true)}>
        <span className="gn-med"><img src={`${GEN}/med_situation.png`} alt="" /></span>
        <span className="gn-m">
          <span className="gn-eyebrow">Ситуация дня</span>
          <h2>{hub.situation.title}</h2>
          <p>{hub.situation.shortText}</p>
          <span className="gn-more">Читать полностью ›</span>
        </span>
      </button>

      <div className="gn-panel">
        <span className="gn-eyebrow">Состояние мира</span>
        <div className="gn-res-grid">
          {hub.resources.slice(0, 5).map((r) => (
            <div className={`gn-res ${r.state === "LOW" ? "low" : r.state === "WATCH" ? "watch" : ""}`} key={r.code}>
              <img src={`${GEN}/${resourceIcon(r.code, r.label)}.png`} alt="" />
              <b>{r.currentValue}/{r.maxValue}</b>
              <span>{r.label}</span>
            </div>
          ))}
        </div>
        {hub.risks.length > 0 && (
          <div className="gn-risks">
            {hub.risks.slice(0, 3).map((rk) => (
              <div className="gn-risk" key={rk.code}>
                <img src={`${GEN}/${riskIcon(rk.code, rk.label)}.png`} alt="" />
                <b>{rk.label}</b>
                <small>{rk.severityLabel}</small>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="gn-panel gn-phase">
        <span className="gn-eyebrow">Сейчас — {phaseInfo(cycle.phase).label}</span>
        <h3>{canAct ? `У вас ${remaining} ${movesPlural(remaining)}` : hub.situation.mainObjective || hub.primaryCta.label}</h3>
        <p className="gn-sub">{hub.primaryCta.hint || hub.situation.mainObjective || "Следите за развитием событий и готовьте следующий шаг."}</p>
        <button
          type="button"
          className="gn-cta"
          onClick={() => {
            const t = ctaTarget(hub.primaryCta.targetSection);
            onNavigate(t.tab, t.ws);
          }}
        >
          {hub.primaryCta.label}
        </button>
      </div>

      {situationOpen && <SituationModal situation={hub.situation} onClose={() => setSituationOpen(false)} />}
    </div>
  );
}
